/**
 * collection.js
 *
 * Four small, independent behaviors for the Collection page, none of
 * which have a clean JS-free equivalent that still matches the
 * reference's own interaction:
 *
 *   - initFilterPanel: the mobile filter button opens `.filters` as a
 *     slide-in panel with a scrim (a SIBLING element, not nested inside
 *     the panel — see main-collection.liquid/collection.css for why),
 *     closes on the scrim/close-button/Escape, locks background scroll
 *     via a Collection-specific `body.mvc-collection-scroll-lock` class
 *     (deliberately NOT the shared `body.lock` class assets/drawer.js's
 *     Cart Drawer/Mobile Menu use, so the two can never interfere with
 *     each other's independent lock lifecycle), and returns focus to
 *     the filter trigger on close. NOT built on this project's existing
 *     MvcDrawer contract (assets/drawer.js) — see main-collection.
 *     liquid's own header comment for why: `.filters` is a dual-purpose
 *     element (a normal static sidebar on desktop, a conditional mobile
 *     panel only within its own CSS breakpoint), and MvcDrawer's
 *     contract assumes a panel that is always `inert`/`aria-hidden`
 *     until opened, which would be wrong for a permanently-interactive
 *     desktop sidebar. This toggles a plain `.on` class only — the same
 *     technique the reference's own `.filters`/`.scrim` already use,
 *     just reusable/idempotent.
 *
 *   - initFilterGroups: each `.fgb` toggles its parent `.fg`'s `.open`
 *     class — a pure presentational accordion, ported directly from the
 *     reference's own identical (trivial, one-line) behavior.
 *
 *   - initSortAutoSubmit: the sort `<select>` submits its associated
 *     form on change (via the HTML5 `form="..."` attribute, since the
 *     select itself lives in the toolbar while the shared filter form
 *     element lives elsewhere in the DOM) — this is the one place a
 *     couple of lines of JS meaningfully improves on requiring a
 *     separate "apply" click, matching the reference's own auto-
 *     updating sort behavior.
 *
 * All four follow this project's established `initX(root)` /
 * `dataset.mvcBound` bound-once convention (homepage.js/policy-hub.js),
 * safe to re-run on `shopify:section:load`. No fetch/XHR/Ajax anywhere
 * — every filter/sort/pagination action is a real link or real form
 * GET submission; Shopify computes the actual result server-side.
 */
(function () {
  'use strict';

  function initFilterPanel(root) {
    var panel = root.querySelector('[data-collection-filters]');
    if (!panel || panel.dataset.mvcBound) return;
    panel.dataset.mvcBound = 'true';

    var trigger = root.querySelector('[id^="CollectionFilterTrigger-"]');
    // `.scrim` is a sibling of the panel in the DOM (not nested inside
    // it), so it is looked up from `root`, not `panel`.
    var scrim = root.querySelector('[data-collection-filters-scrim]');
    var closeBtn = panel.querySelector('[data-collection-filters-close]');
    var LOCK_CLASS = 'mvc-collection-scroll-lock';

    function open() {
      panel.classList.add('on');
      if (scrim) scrim.classList.add('on');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add(LOCK_CLASS);
    }
    function close() {
      panel.classList.remove('on');
      if (scrim) scrim.classList.remove('on');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove(LOCK_CLASS);
      if (trigger) trigger.focus();
    }

    if (trigger) trigger.addEventListener('click', open);
    if (scrim) scrim.addEventListener('click', close);
    if (closeBtn) closeBtn.addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });
    // Safety: if Theme Editor unloads this section while the panel is
    // open, clear the scroll lock rather than leaving the storefront
    // permanently unscrollable.
    document.addEventListener('shopify:section:unload', function (e) {
      var sectionId = e && e.detail && e.detail.sectionId;
      if (!sectionId) return;
      var wrapper = document.getElementById('shopify-section-' + sectionId);
      if (wrapper && wrapper.contains(root)) {
        document.body.classList.remove(LOCK_CLASS);
      }
    });
  }

  function initFilterGroups(root) {
    var toggles = root.querySelectorAll('[data-collection-filter-toggle]');
    toggles.forEach(function (btn) {
      if (btn.dataset.mvcBound) return;
      btn.dataset.mvcBound = 'true';
      btn.addEventListener('click', function () {
        var group = btn.closest('.fg');
        if (group) group.classList.toggle('open');
      });
    });
  }

  function initSortAutoSubmit(root) {
    var select = root.querySelector('[data-collection-sort]');
    if (!select || select.dataset.mvcBound) return;
    select.dataset.mvcBound = 'true';
    select.addEventListener('change', function () {
      var form = select.form;
      if (form) form.submit();
    });
  }

  function initLoadMore(root) {
    var button = root.querySelector('[data-collection-load-more]');
    var grid = root.querySelector('[data-collection-product-grid]');
    if (!button || !grid || button.dataset.mvcBound) return;
    button.dataset.mvcBound = 'true';

    var label = button.querySelector('[data-collection-load-more-label]');
    var status = root.querySelector('[data-collection-load-more-status]');
    var visibleCount = root.querySelector('[data-collection-visible-count]');
    var loading = false;
    var defaultLabel = label ? label.textContent : button.textContent;
    var loadingLabel = button.getAttribute('data-loading-label') || 'טוען מוצרים…';

    function setLoading(isLoading) {
      loading = isLoading;
      button.classList.toggle('is-loading', isLoading);
      button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
      if (label) label.textContent = isLoading ? loadingLabel : defaultLabel;
    }

    button.addEventListener('click', function (event) {
      event.preventDefault();
      if (loading) return;

      var nextUrl = button.getAttribute('href');
      if (!nextUrl) return;

      setLoading(true);
      if (status) status.textContent = 'טוען מוצרים נוספים';

      fetch(nextUrl, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Collection page request failed');
          return response.text();
        })
        .then(function (html) {
          var page = new DOMParser().parseFromString(html, 'text/html');
          var nextRoot = page.querySelector('.mvc-collection');
          var nextGrid = nextRoot && nextRoot.querySelector('[data-collection-product-grid]');
          if (!nextGrid) throw new Error('Next collection grid was not found');

          var newCards = Array.prototype.slice.call(nextGrid.children);
          var fragment = document.createDocumentFragment();
          newCards.forEach(function (card) {
            fragment.appendChild(card);
          });
          grid.appendChild(fragment);

          var currentVisible = parseInt(visibleCount && visibleCount.textContent, 10) || 0;
          if (visibleCount) visibleCount.textContent = String(currentVisible + newCards.length);

          var nextButton = nextRoot.querySelector('[data-collection-load-more]');
          if (nextButton) {
            button.setAttribute('href', nextButton.getAttribute('href'));
            setLoading(false);
            if (status) status.textContent = newCards.length + ' מוצרים נוספים נטענו';
          } else {
            button.remove();
            if (status) status.textContent = 'כל המוצרים מוצגים';
          }

          root.dispatchEvent(new CustomEvent('mvc:collection:products-loaded', {
            bubbles: true,
            detail: { count: newCards.length }
          }));
        })
        .catch(function () {
          setLoading(false);
          if (status) status.textContent = 'לא הצלחנו לטעון מוצרים נוספים. אפשר לנסות שוב.';
        });
    });
  }

  function init() {
    document.querySelectorAll('.mvc-collection').forEach(function (root) {
      initFilterPanel(root);
      initFilterGroups(root);
      initSortAutoSubmit(root);
      initLoadMore(root);
    });
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
